import { useState, useEffect, useCallback } from 'react';

const WMO_CODES = {
  0: 'Clear', 1: 'Mostly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Rime Fog',
  51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
  61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
  66: 'Freezing Rain', 67: 'Heavy Freezing Rain',
  71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow', 77: 'Snow Grains',
  80: 'Light Showers', 81: 'Showers', 82: 'Heavy Showers',
  85: 'Light Snow Showers', 86: 'Heavy Snow Showers',
  95: 'Thunderstorm', 96: 'Thunderstorm w/ Hail', 99: 'Thunderstorm w/ Heavy Hail',
};

const WIND_DIR = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
function degToDir(deg) { return WIND_DIR[Math.round(deg / 22.5) % 16]; }

/* Heat index (Rothfusz regression, NWS formula) — valid when temp >= 80°F */
function calcHeatIndex(tempF, rh) {
  if (tempF < 80) return tempF;
  let hi = -42.379 + 2.04901523 * tempF + 10.14333127 * rh
    - 0.22475541 * tempF * rh - 0.00683783 * tempF * tempF
    - 0.05481717 * rh * rh + 0.00122874 * tempF * tempF * rh
    + 0.00085282 * tempF * rh * rh - 0.00000199 * tempF * tempF * rh * rh;
  if (rh < 13 && tempF >= 80 && tempF <= 112) {
    hi -= ((13 - rh) / 4) * Math.sqrt((17 - Math.abs(tempF - 95)) / 17);
  } else if (rh > 85 && tempF >= 80 && tempF <= 87) {
    hi += ((rh - 85) / 10) * ((87 - tempF) / 5);
  }
  return Math.round(hi);
}

/* Wind chill (NWS formula) — valid when temp <= 50°F and wind >= 3 mph */
function calcWindChill(tempF, windMph) {
  if (tempF > 50 || windMph < 3) return tempF;
  return Math.round(
    35.74 + 0.6215 * tempF - 35.75 * Math.pow(windMph, 0.16) + 0.4275 * tempF * Math.pow(windMph, 0.16)
  );
}

/* WBGT estimate (simplified outdoor — Liljegren approximation) */
function estimateWBGT(tempF, rh, windMph) {
  const tempC = (tempF - 32) * 5 / 9;
  const tw = tempC * Math.atan(0.151977 * Math.sqrt(rh + 8.313659))
    + Math.atan(tempC + rh) - Math.atan(rh - 1.676331)
    + 0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) - 4.686035;
  const tg = tempC + 1.5; // simplified globe temp (no solar radiation data)
  const wbgt = 0.7 * tw + 0.2 * tg + 0.1 * tempC;
  return Math.round(wbgt * 10) / 10;
}

/* Rehab guidance based on WBGT (°C) */
function getRehabGuidance(wbgtC) {
  if (wbgtC >= 32.2) return { level: 'EXTREME', col: '#ef4444', work: '15 min', rest: '45 min', note: 'Consider suspending non-critical ops. Mandatory rehab after every SCBA bottle.' };
  if (wbgtC >= 29.4) return { level: 'HIGH', col: '#f97316', work: '20 min', rest: '40 min', note: 'Aggressive rehab. Monitor all personnel for heat illness. Shorten work cycles.' };
  if (wbgtC >= 25.6) return { level: 'MODERATE', col: '#eab308', work: '30 min', rest: '30 min', note: 'Standard rehab protocols. Ensure hydration and shade available.' };
  if (wbgtC >= 21.1) return { level: 'LOW', col: '#22c55e', work: '45 min', rest: '15 min', note: 'Normal operations. Standard hydration.' };
  return { level: 'MINIMAL', col: '#94a3b8', work: '—', rest: '—', note: 'No heat-related restrictions.' };
}

function parseWeather(data) {
  const c = data.current;
  const d = data.daily;
  const temp = Math.round(c.temperature_2m);
  const humidity = c.relative_humidity_2m;
  const windSpeed = Math.round(c.wind_speed_10m);
  const gusts = Math.round(c.wind_gusts_10m);
  const heatIndex = calcHeatIndex(temp, humidity);
  const windChill = calcWindChill(temp, windSpeed);
  const wbgt = estimateWBGT(temp, humidity, windSpeed);
  const rehab = getRehabGuidance(wbgt);

  // Apparent / "feels like"
  let feelsLike = temp;
  if (temp >= 80) feelsLike = heatIndex;
  else if (temp <= 50 && windSpeed >= 3) feelsLike = windChill;

  return {
    temp,
    humidity,
    windSpeed,
    windDir: degToDir(c.wind_direction_10m),
    windDeg: c.wind_direction_10m,
    gusts,
    condition: WMO_CODES[c.weather_code] || 'Unknown',
    code: c.weather_code,
    heatIndex,
    windChill,
    feelsLike,
    wbgt,
    wbgtF: Math.round(wbgt * 9 / 5 + 32),
    rehab,
    // Extended current data
    dewPoint: c.dew_point_2m != null ? Math.round(c.dew_point_2m) : null,
    apparentTemp: c.apparent_temperature != null ? Math.round(c.apparent_temperature) : null,
    pressure: c.surface_pressure != null ? Math.round(c.surface_pressure) : null,
    precipitation: c.precipitation != null ? c.precipitation : null,
    cloudCover: c.cloud_cover != null ? c.cloud_cover : null,
    visibility: c.visibility != null ? c.visibility : null,
    uvIndex: c.uv_index != null ? Math.round(c.uv_index * 10) / 10 : null,
    // Daily summary
    high: d?.temperature_2m_max?.[0] != null ? Math.round(d.temperature_2m_max[0]) : null,
    low: d?.temperature_2m_min?.[0] != null ? Math.round(d.temperature_2m_min[0]) : null,
    dailyPrecip: d?.precipitation_sum?.[0] != null ? d.precipitation_sum[0] : null,
    maxWind: d?.wind_speed_10m_max?.[0] != null ? Math.round(d.wind_speed_10m_max[0]) : null,
    uvMax: d?.uv_index_max?.[0] != null ? Math.round(d.uv_index_max[0] * 10) / 10 : null,
    sunrise: d?.sunrise?.[0] || null,
    sunset: d?.sunset?.[0] || null,
  };
}

export function useWeather(lat, lng) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const fetchAlerts = useCallback(async (latitude, longitude) => {
    try {
      // NWS alerts for the point
      const resp = await fetch(
        `https://api.weather.gov/alerts/active?point=${latitude},${longitude}`,
        { headers: { 'User-Agent': 'HydroCalc-CMFE/5.0' } }
      );
      if (!resp.ok) return;
      const data = await resp.json();
      const parsed = (data.features || []).map((f) => ({
        id: f.properties.id,
        event: f.properties.event,
        severity: f.properties.severity,
        headline: f.properties.headline,
        description: f.properties.description,
        urgency: f.properties.urgency,
      }));
      setAlerts(parsed);
    } catch {
      // Alerts are best-effort
    }
  }, []);

  useEffect(() => {
    // Use null checks, not falsy — lat/lng of 0 (equator / prime meridian) are valid.
    if (lat == null || lng == null) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,relative_humidity_2m,apparent_temperature,dew_point_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m,weather_code,surface_pressure,precipitation,cloud_cover,visibility,uv_index&daily=temperature_2m_max,temperature_2m_min,precipitation_sum,wind_speed_10m_max,uv_index_max,sunrise,sunset&temperature_unit=fahrenheit&wind_speed_unit=mph&precipitation_unit=inch&timezone=auto&forecast_days=1`;

    fetch(url)
      .then((r) => { if (!r.ok) throw new Error('Weather fetch failed'); return r.json(); })
      .then((data) => {
        if (cancelled) return;
        setWeather(parseWeather(data));
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message);
        setLoading(false);
      });

    fetchAlerts(lat, lng);

    // Refresh every 10 minutes. Background refresh failures are logged
    // but kept silent in the UI (the existing forecast stays visible).
    const interval = setInterval(() => {
      fetch(url)
        .then((r) => r.json())
        .then((data) => {
          if (cancelled) return;
          setWeather(parseWeather(data));
        })
        .catch((err) => {
          console.warn('[useWeather] background refresh failed:', err);
        });
      fetchAlerts(lat, lng);
    }, 600000);

    return () => { cancelled = true; clearInterval(interval); };
  }, [lat, lng, fetchAlerts]);

  return { weather, loading, error, alerts };
}
